import { type ReactNode, useState } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import {
  FluentProvider,
  makeStyles,
  tokens,
  Text,
  Button,
  Divider,
  Avatar,
} from '@fluentui/react-components';
import {
  FormNew24Regular,
  ClipboardBulletListLtrRegular,
  DataTrending24Regular,
  Navigation24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { appTheme } from '@/theme';
import { useAuth } from '@/components/auth/AuthProvider';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },

  // Top bar
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    minHeight: '48px',
    flexShrink: 0,
    zIndex: 10,
  },
  topBarTitle: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    flexGrow: 1,
  },
  menuButton: {
    color: tokens.colorNeutralForegroundOnBrand,
    // Only visible on mobile
    '@media (min-width: 768px)': {
      display: 'none',
    },
  },
  userChip: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForegroundOnBrand,
    // Hide name text on very small screens
    '@media (max-width: 480px)': {
      '& span': {
        display: 'none',
      },
    },
  },

  // Body: sidebar + main content
  body: {
    display: 'flex',
    flexGrow: 1,
    overflow: 'hidden',
  },

  // Sidebar
  sidebar: {
    width: '220px',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: tokens.colorNeutralStroke2,
    display: 'flex',
    flexDirection: 'column',
    padding: `${tokens.spacingVerticalM} 0`,
    overflowY: 'auto',
    // Hidden on mobile unless open
    '@media (max-width: 767px)': {
      position: 'absolute',
      top: '48px',
      left: 0,
      bottom: 0,
      zIndex: 20,
      transform: 'translateX(-100%)',
      transition: 'transform 200ms ease',
      boxShadow: tokens.shadow16,
    },
  },
  sidebarOpen: {
    '@media (max-width: 767px)': {
      transform: 'translateX(0)',
    },
  },
  overlay: {
    display: 'none',
    '@media (max-width: 767px)': {
      display: 'block',
      position: 'absolute',
      top: '48px',
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.4)',
      zIndex: 15,
    },
  },

  navSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: `0 ${tokens.spacingHorizontalS}`,
  },
  navLabel: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    border: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    textAlign: 'left',
    minHeight: '44px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
  },
  navItemActive: {
    backgroundColor: tokens.colorSubtleBackgroundSelected,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundColor: tokens.colorSubtleBackgroundSelected,
    },
  },

  // Main content
  main: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: tokens.spacingVerticalL,
    '@media (max-width: 767px)': {
      padding: tokens.spacingVerticalM,
    },
  },
});

// ─── Nav item definition ──────────────────────────────────────────────────────

interface NavItemDef {
  label: string;
  icon: ReactNode;
  path: string;
  adminOnly?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { userName, userEmail } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems: NavItemDef[] = [
    {
      label: 'Submit Referral',
      icon: <FormNew24Regular />,
      path: '/referrals/new',
    },
    {
      label: 'My Referrals',
      icon: <ClipboardBulletListLtrRegular />,
      path: '/referrals/mine',
    },
    {
      label: 'Admin',
      icon: <DataTrending24Regular />,
      path: '/admin',
      adminOnly: false, // Show to all for now; restrict via backend roles
    },
  ];

  const handleNav = (path: string) => {
    void navigate({ to: path });
    setSidebarOpen(false);
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  const displayName = userName ?? userEmail ?? 'User';
  const initials = displayName
    .split(' ')
    .map((w) => w[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <FluentProvider theme={appTheme}>
      <div className={styles.root}>
        {/* Top bar */}
        <header className={styles.topBar}>
          <Button
            className={styles.menuButton}
            appearance="transparent"
            icon={sidebarOpen ? <Dismiss24Regular /> : <Navigation24Regular />}
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label={sidebarOpen ? 'Close navigation' : 'Open navigation'}
          />
          <Text className={styles.topBarTitle}>ATA Cross-Sell Referrals</Text>
          <div className={styles.userChip}>
            <Avatar name={displayName} initials={initials} size={28} color="colorful" />
            <span>{displayName}</span>
          </div>
        </header>

        <div className={styles.body}>
          {/* Mobile overlay */}
          {sidebarOpen && (
            <div
              className={styles.overlay}
              onClick={() => setSidebarOpen(false)}
              role="presentation"
            />
          )}

          {/* Sidebar navigation */}
          <nav
            className={`${styles.sidebar}${sidebarOpen ? ` ${styles.sidebarOpen}` : ''}`}
            aria-label="Main navigation"
          >
            <div className={styles.navSection}>
              <Text className={styles.navLabel}>Navigation</Text>
              {navItems.map((item) => (
                <button
                  key={item.path}
                  className={`${styles.navItem}${isActive(item.path) ? ` ${styles.navItemActive}` : ''}`}
                  onClick={() => handleNav(item.path)}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  type="button"
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>

            <Divider style={{ margin: `${tokens.spacingVerticalM} 0` }} />

            <div
              style={{
                padding: `0 ${tokens.spacingHorizontalM}`,
                marginTop: 'auto',
              }}
            >
              <Text
                size={200}
                style={{ color: tokens.colorNeutralForeground3, wordBreak: 'break-all' }}
              >
                {userEmail}
              </Text>
            </div>
          </nav>

          {/* Page content */}
          <main className={styles.main}>{children}</main>
        </div>
      </div>
    </FluentProvider>
  );
}
